import * as React from "react";
import {useState} from "react";
import {Alert, Badge, Card, Collapse, Divider, Progress, Table, Tag, Typography} from "antd";
import {ArrowUpOutlined, ArrowDownOutlined, MinusOutlined} from "@ant-design/icons";
import {SecureMarkdownRenderer} from "./SecureMarkdownRenderer";
import JsonViewer from "./JsonViewer";
import './GenUIRenderer.css';

const {Title, Paragraph, Text} = Typography;

/** CSS class mapping for each component type */
export enum GenUIClass {
    Renderer    = 'genui-renderer',
    Title       = 'genui-title',
    Text        = 'genui-text',
    Markdown    = 'genui-markdown',
    Table       = 'genui-table',
    Metric      = 'genui-metric',
    Alert       = 'genui-alert',
    KeyValue    = 'genui-key-value',
    Code        = 'genui-code',
    Json        = 'genui-json',
    List        = 'genui-list',
    Tag         = 'genui-tag',
    Badges      = 'genui-badges',
    Divider     = 'genui-divider',
    Section     = 'genui-section',
    Card        = 'genui-card',
    Grid        = 'genui-grid',
    Collapse    = 'genui-collapse',
    Progress    = 'genui-progress',
}

export interface GenUIComponent {
    type: string;
    [key: string]: any;
}

interface GenUIRendererProps {
    components: GenUIComponent[] | Record<string | number, GenUIComponent>;
}

const trendIcons: Record<string, React.ReactNode> = {
    up: <ArrowUpOutlined style={{color: '#52c41a'}}/>,
    down: <ArrowDownOutlined style={{color: '#ff4d4f'}}/>,
    neutral: <MinusOutlined style={{color: '#8c8c8c'}}/>,
};

/**
 * Normalize components input: accepts an array or a dict keyed by order numbers.
 */
function normalizeComponents(components: GenUIComponent[] | Record<string | number, GenUIComponent>): GenUIComponent[] {
    if (Array.isArray(components)) return components;
    if (components && typeof components === 'object') {
        return Object.keys(components)
            .sort((a, b) => Number(a) - Number(b))
            .map(key => components[key]);
    }
    return [];
}

const GenUIRenderer = ({components}: GenUIRendererProps) => {
    const items = normalizeComponents(components);
    if (!items.length) return null;

    return (
        <div className={GenUIClass.Renderer}>
            {items.map((c, i) => (
                <GenUIElement key={i} component={c}/>
            ))}
        </div>
    );
};

const GenUIElement = ({component: c}: { component: GenUIComponent }) => {
    switch (c.type) {
        case 'title':
            return (
                <Title className={GenUIClass.Title} level={Number(c.level || 3) as 1 | 2 | 3 | 4 | 5}>
                    {c.text}
                </Title>
            );

        case 'text':
            return (
                <Paragraph
                    className={GenUIClass.Text}
                    type={c.text_type && c.text_type !== 'default' ? c.text_type : undefined}
                >
                    {c.content}
                </Paragraph>
            );

        case 'markdown':
            return (
                <div className={GenUIClass.Markdown}>
                    <SecureMarkdownRenderer content={c.content}/>
                </div>
            );

        case 'table':
            return (
                <Table
                    className={GenUIClass.Table}
                    columns={c.columns.map((col: string | { title: string; key: string }) =>
                        typeof col === 'string'
                            ? {title: col, dataIndex: col, key: col}
                            : {title: col.title, dataIndex: col.key, key: col.key}
                    )}
                    dataSource={
                        Array.isArray(c.rows)
                            ? c.rows.map((row: any, ri: number) => {
                                if (Array.isArray(row)) {
                                    return {
                                        key: ri,
                                        ...Object.fromEntries(
                                            c.columns.map((col: string | { key: string }, ci: number) => [
                                                typeof col === 'string' ? col : col.key,
                                                row[ci]
                                            ])
                                        )
                                    };
                                }
                                return {...row, key: row.key ?? ri};
                            })
                            : []
                    }
                    pagination={false}
                    size={c.size || 'small'}
                />
            );

        case 'metric':
            return (
                <div className={GenUIClass.Metric}>
                    <Text className="genui-metric-label" type="secondary">{c.label}</Text>
                    <div className="genui-metric-value-row">
                        <span className="genui-metric-value">{c.value}</span>
                        {c.suffix && <span className="genui-metric-suffix"> {c.suffix}</span>}
                        {c.trend && trendIcons[c.trend]}
                    </div>
                    {c.trend_label && (
                        <Text className="genui-metric-trend-label" type="secondary">{c.trend_label}</Text>
                    )}
                </div>
            );

        case 'alert':
            return (
                <Alert
                    className={GenUIClass.Alert}
                    type={c.alert_type || 'info'}
                    message={c.message}
                    description={c.description || undefined}
                    showIcon
                />
            );

        case 'key_value':
            return (
                <div className={GenUIClass.KeyValue}>
                    {c.items?.map((item: { label: string; value: any }, j: number) => (
                        <React.Fragment key={j}>
                            <Text type="secondary" strong>{item.label}</Text>
                            <Text>{String(item.value)}</Text>
                        </React.Fragment>
                    ))}
                </div>
            );

        case 'code':
            return (
                <pre className={GenUIClass.Code}>
                    <code>{typeof c.code === 'string' ? c.code : JSON.stringify(c.code, null, 2)}</code>
                </pre>
            );

        case 'json':
            return (
                <div className={GenUIClass.Json}>
                    <JsonViewer data={c.data}/>
                </div>
            );

        case 'list': {
            const ListTag = c.ordered ? 'ol' : 'ul';
            return (
                <ListTag className={GenUIClass.List}>
                    {c.items?.map((item: string, j: number) => (
                        <li key={j}>{item}</li>
                    ))}
                </ListTag>
            );
        }

        case 'tag':
            return <Tag className={GenUIClass.Tag} color={c.color || undefined}>{c.text}</Tag>;

        case 'badges':
            return (
                <div className={GenUIClass.Badges}>
                    {c.items?.map((item: { text: string; status?: string }, j: number) => (
                        <Badge key={j} status={(item.status || 'default') as any} text={item.text}/>
                    ))}
                </div>
            );

        case 'divider':
            return <Divider>{c.text}</Divider>;

        case 'section':
            return (
                <div className={GenUIClass.Section}>
                    {c.title && <Title className="genui-section-title" level={4}>{c.title}</Title>}
                    {c.components && <GenUIRenderer components={c.components}/>}
                </div>
            );

        case 'card':
            return (
                <Card className={GenUIClass.Card} title={c.title} bordered={c.bordered ?? true}>
                    {c.components && <GenUIRenderer components={c.components}/>}
                </Card>
            );

        case 'grid':
            return (
                <div
                    className={GenUIClass.Grid}
                    style={{
                        gridTemplateColumns: `repeat(${c.columns || 2}, 1fr)`,
                        gap: c.gap ?? 16,
                    }}
                >
                    {c.components?.map((child: GenUIComponent, j: number) => (
                        <div key={j}>
                            <GenUIElement component={child}/>
                        </div>
                    ))}
                </div>
            );

        case 'collapse':
            return <CollapseElement component={c}/>;

        case 'progress':
            return (
                <div className={GenUIClass.Progress}>
                    {c.label && <Text className="genui-progress-label">{c.label}</Text>}
                    <Progress percent={c.percent} status={c.status || undefined}/>
                </div>
            );

        default:
            console.warn('[GenUI] Unknown component type, skipping:', c.type, c);
            return null;
    }
};

/** Collapse needs local state, so it's a separate component */
const CollapseElement = ({component: c}: { component: GenUIComponent }) => {
    const [open, setOpen] = useState(c.default_open ?? false);
    return (
        <Collapse
            className={GenUIClass.Collapse}
            activeKey={open ? ['1'] : []}
            onChange={() => setOpen(!open)}
            items={[{
                key: '1',
                label: c.title,
                children: c.components ? <GenUIRenderer components={c.components}/> : null,
            }]}
        />
    );
};

/**
 * JSON Schema catalog describing the component spec that the LLM must return.
 * Send this as part of the system prompt so the model knows the exact contract.
 *
 * The schema expects { "components": [ ... ] } — an array of component objects.
 * If the LLM returns a dict keyed by order numbers instead, the renderer
 * normalizes it to an array at runtime via normalizeComponents().
 */
export const GEN_UI_CATALOG_SCHEMA = {
    type: "object",
    required: ["components"],
    properties: {
        components: {
            type: "array",
            items: {
                type: "object",
                required: ["type"],
                oneOf: [
                    {
                        properties: {
                            type: {const: "title"},
                            text: {type: "string", description: "Heading text"},
                            level: {type: "number", enum: [1, 2, 3, 4], description: "Heading level (default: 3)"},
                        },
                        required: ["type", "text"],
                    },
                    {
                        properties: {
                            type: {const: "text"},
                            content: {type: "string", description: "Paragraph text"},
                            text_type: {type: "string", enum: ["default", "secondary", "success", "warning", "danger"], description: "Text style variant"},
                        },
                        required: ["type", "content"],
                    },
                    {
                        properties: {
                            type: {const: "markdown"},
                            content: {type: "string", description: "Markdown content"},
                        },
                        required: ["type", "content"],
                    },
                    {
                        properties: {
                            type: {const: "table"},
                            columns: {
                                type: "array",
                                items: {
                                    oneOf: [
                                        {type: "string"},
                                        {type: "object", properties: {title: {type: "string"}, key: {type: "string"}}, required: ["title", "key"]},
                                    ],
                                },
                                description: "Column headers as strings or {title, key} objects",
                            },
                            rows: {
                                type: "array",
                                items: {
                                    oneOf: [
                                        {type: "array", items: {}},
                                        {type: "object"},
                                    ],
                                },
                                description: "Rows as arrays (positional) or objects (keyed by column key)",
                            },
                            size: {type: "string", enum: ["small", "middle", "large"]},
                        },
                        required: ["type", "columns", "rows"],
                    },
                    {
                        properties: {
                            type: {const: "metric"},
                            label: {type: "string", description: "Metric label"},
                            value: {oneOf: [{type: "string"}, {type: "number"}], description: "Metric value"},
                            suffix: {type: "string", description: "Unit or suffix, e.g. '%', 'ms', 'GB'"},
                            trend: {type: "string", enum: ["up", "down", "neutral"], description: "Trend direction arrow"},
                            trend_label: {type: "string", description: "Trend description, e.g. '+12% from last week'"},
                        },
                        required: ["type", "label", "value"],
                    },
                    {
                        properties: {
                            type: {const: "alert"},
                            alert_type: {type: "string", enum: ["success", "info", "warning", "error"]},
                            message: {type: "string", description: "Alert title/message"},
                            description: {type: "string", description: "Detailed description below the message"},
                        },
                        required: ["type", "alert_type", "message"],
                    },
                    {
                        properties: {
                            type: {const: "key_value"},
                            items: {
                                type: "array",
                                items: {
                                    type: "object",
                                    properties: {
                                        label: {type: "string"},
                                        value: {oneOf: [{type: "string"}, {type: "number"}, {type: "boolean"}]},
                                    },
                                    required: ["label", "value"],
                                },
                                description: "List of label-value pairs",
                            },
                        },
                        required: ["type", "items"],
                    },
                    {
                        properties: {
                            type: {const: "code"},
                            code: {type: "string", description: "Code content"},
                            language: {type: "string", description: "Language for syntax highlighting, e.g. 'json', 'python'"},
                        },
                        required: ["type", "code"],
                    },
                    {
                        properties: {
                            type: {const: "json"},
                            data: {description: "JSON data to display in an interactive tree viewer"},
                        },
                        required: ["type", "data"],
                    },
                    {
                        properties: {
                            type: {const: "list"},
                            items: {type: "array", items: {type: "string"}, description: "List items"},
                            ordered: {type: "boolean", description: "If true, renders as numbered list"},
                        },
                        required: ["type", "items"],
                    },
                    {
                        properties: {
                            type: {const: "tag"},
                            text: {type: "string", description: "Tag label"},
                            color: {type: "string", enum: ["blue", "green", "red", "orange", "gray", "purple", "cyan"], description: "Tag color"},
                        },
                        required: ["type", "text"],
                    },
                    {
                        properties: {
                            type: {const: "badges"},
                            items: {
                                type: "array",
                                items: {
                                    type: "object",
                                    properties: {
                                        text: {type: "string"},
                                        status: {type: "string", enum: ["success", "processing", "error", "warning", "default"]},
                                    },
                                    required: ["text"],
                                },
                                description: "List of status badges",
                            },
                        },
                        required: ["type", "items"],
                    },
                    {
                        properties: {
                            type: {const: "divider"},
                            text: {type: "string", description: "Optional text shown on the divider line"},
                        },
                        required: ["type"],
                    },
                    {
                        properties: {
                            type: {const: "progress"},
                            percent: {type: "number", minimum: 0, maximum: 100, description: "Progress percentage"},
                            status: {type: "string", enum: ["success", "active", "exception", "normal"]},
                            label: {type: "string", description: "Label text next to the bar"},
                        },
                        required: ["type", "percent"],
                    },
                    {
                        properties: {
                            type: {const: "section"},
                            title: {type: "string", description: "Section heading"},
                            components: {type: "array", items: {}, description: "Nested components (same schema)"},
                        },
                        required: ["type"],
                    },
                    {
                        properties: {
                            type: {const: "card"},
                            title: {type: "string", description: "Card title"},
                            bordered: {type: "boolean"},
                            components: {type: "array", items: {}, description: "Nested components (same schema)"},
                        },
                        required: ["type", "title"],
                    },
                    {
                        properties: {
                            type: {const: "grid"},
                            columns: {type: "number", minimum: 1, maximum: 4, description: "Number of columns (1-4)"},
                            gap: {type: "number", description: "Gap between items in pixels"},
                            components: {type: "array", items: {}, description: "Nested components (same schema)"},
                        },
                        required: ["type", "components"],
                    },
                    {
                        properties: {
                            type: {const: "collapse"},
                            title: {type: "string", description: "Collapse panel header text"},
                            default_open: {type: "boolean", description: "Whether panel starts expanded"},
                            components: {type: "array", items: {}, description: "Nested components (same schema)"},
                        },
                        required: ["type", "title"],
                    },
                ],
            },
        },
    },
};

export default GenUIRenderer;
